import {
    Component,
    OnInit,
    ViewEncapsulation,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    HostListener,
} from '@angular/core';

import { ProductService } from '../../../services/product.service';
import { FilterGroup } from '../../../models/filter-group.model';
import { AppSettings } from '../../../app.settings';
import { HEADER_HEIGHT, LocalStorageService } from '../../../services/local-storage.service';
import {isEmptyArray, updateItemInArray} from '../../../shared/utils';
import {MobileCheckServiceService} from '../../../services/mobile-check-service.service';

const PADDING = 15;
const SCROLL_WIDTH = 16
const FOOTER_HEIGHT = 104;
const FILTER_PADDING = 15;
const MAX_MOBILE_WIDTH = 375;
@Component({
    selector: 'products-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    encapsulation: ViewEncapsulation.None
})

// ! NOTE - the "1" is tmp, should be the product group ID
// "1" is the default for eComm

export class FilterComponent implements OnInit {

    @Output() update = new EventEmitter();

    mobileOpened = false;
    // isBottom = false;

    fixedPosition = false; // in case fixHeader = true, the component should be  fixed on top
    private offsetTop = 0;
    private maxHeight = window.innerHeight;
    private filterMaxHeight = 200;
    private componentWidth = 200;
    private componentLeftOffset = PADDING;
    private componentTopOffset = 0;
    // private componentBottomOffset = 0;
    private parentElement: HTMLElement;
    private filterItemMaxHeight = 160;

    _filters: FilterGroup[] = [];
    @Input() public set filters(val: FilterGroup[]) {
        if (!isEmptyArray(val)) {
            this._filters = val.map( v => new FilterGroup(v));
        } else {
            this._filters = [];
        }
    }

    constructor(protected productService: ProductService,
                private elementRef: ElementRef,
                private appSettings: AppSettings,
                private localService: LocalStorageService,
                private mobileCheck: MobileCheckServiceService) {
    }

    ngOnInit() {
        this.parentElement = this.elementRef.nativeElement.parentElement;

        this.calcComponentPosition();
    }

    updateFilters(filterGroup: FilterGroup) {
        if (!filterGroup) {
            return;
        }
        // close all filters
        this._filters.forEach( f => {
            if (f.expanded) {
                f.expanded = false;
            }
        });

        this._filters = updateItemInArray<FilterGroup>(this._filters, filterGroup);
        this.update.emit(this._filters);
        this.closeMobileFilter();
    }


    toggleDropdown(filterGroup: FilterGroup) {
        // collapse all other filters
        if (filterGroup?.expanded) {
            this._filters.forEach( f => {
                if (f.id !== filterGroup.id) {
                    f.expanded = false;
                }
            });
        }

        this._filters = updateItemInArray<FilterGroup>(this._filters, filterGroup);
        this.calcComponentPosition();
    }

    onFilterChanged(filterGroup: FilterGroup) {
        if (filterGroup.changed) {
            this._filters =  this._filters.map( f => {
               f.changed = true;
               return f;
            });
        }
    }


    @HostListener('window:scroll')
    onWindowScroll() {

        if (this.offsetTop === 0 || window.pageYOffset === 0) {
            this.offsetTop = this.parentElement.getBoundingClientRect().top;
        }
        this.fixedPosition = this.parentElement.getBoundingClientRect().top < +this.localService.getItem(HEADER_HEIGHT);
        this.calcComponentPosition();
    }


    @HostListener('window:resize')
    onWindowResize() {
        this.calcComponentPosition();
    }


    @HostListener('click')
    onClick() {
        this.calcComponentPosition();
    }


    @HostListener('document:mousedown', ['$event'])
    onWindowClick(event) {
        if (this.mobileOpened && !this.elementRef.nativeElement.contains(event.target)) {
            // clicked outside => close dropdown list
            this.toggleFiltersModal();
        }
    }

    get styles() {

        const style = {
            'maxHeight.px': Math.floor(this.maxHeight),
            'width.px': Math.floor(this.componentWidth),
            'overflow-y': 'hidden'
        };
        if (this.fixedPosition && !this.mobile) {
            style['left.px'] = this.componentLeftOffset;
            style['top.px'] = this.componentTopOffset;
        }

        return style;
    }


    // save component width only in non-fixed mode
    calcComponentPosition() {
        if (this.mobile) {
            this.calcMobileComponentPosition();
        } else {
            this.calcDesktopComponentPosition();
        }

    }

    calcMobileComponentPosition() {
        const {top, left} = this.elementRef.nativeElement.getBoundingClientRect()
        const height = window.innerHeight - top;
        const width = window.innerWidth - left
        this.calculateMobileFilterItemHeight(height);
    }

    calcDesktopComponentPosition() {
        const {width, top, bottom, left}: ClientRect = this.parentElement.getBoundingClientRect();
        const headerHeight = +this.localService.getItem(HEADER_HEIGHT);
        this.componentWidth = width - PADDING - SCROLL_WIDTH;
        this.componentLeftOffset = left + PADDING;

        const divRect =  this.getElementRect();
        const minHeight = this.getMinHeight(headerHeight);
        if (!this.fixedPosition) {
            this.maxHeight = Math.max(window.innerHeight - top - 2 * PADDING,
                minHeight);
            this.componentTopOffset = 0;
        } else {
            this.componentTopOffset = headerHeight;
            // bottom of the screen
            if (bottom < window.innerHeight) {
                const availableHeight = Math.max(minHeight, bottom - PADDING - headerHeight);
                this.maxHeight = window.innerHeight - headerHeight - FOOTER_HEIGHT;
            } else {
                // full height screen
                this.maxHeight = window.innerHeight - headerHeight;
                this.componentTopOffset = headerHeight;
            }
        }

        this.calculateFilterItemHeight();

    }

    private calculateFilterItemHeight() {
        const {width, top, bottom, left}: ClientRect = this.parentElement.getBoundingClientRect();
        const divRect =  this.getElementRect();
        let availableHeight = window.innerHeight - divRect.top;
        if (bottom < window.innerHeight) {
            availableHeight -= FOOTER_HEIGHT;
        }

        const filterHeaderHeight = 34;

        const filterMinHeight = this.collapseFiltersHeight + filterHeaderHeight;
        this.filterItemMaxHeight = Math.max(availableHeight - filterMinHeight, 160);
        const selectedFilters =  this._filters ? this._filters.filter( f => f.visible && f.selected).length : 0;

        if (this.filtersCount > 0) {
            const height = this.maxHeight - this.collapseFiltersHeight;
            if (selectedFilters === 0) {
                this.filterMaxHeight = height - FILTER_PADDING;
            } else {
                this.filterMaxHeight = Math.floor( height / selectedFilters) - FILTER_PADDING;
            }


            this.filterMaxHeight = Math.max(this.filterMaxHeight, 120);
        }

    }

    private calculateMobileFilterItemHeight(availableHeight: number) {
        const windowHeight = availableHeight - 30;
        const filterHeaderHeight = 34;
        const filterMinHeight = this.collapseFiltersHeight + filterHeaderHeight;
        this.filterItemMaxHeight = Math.max(windowHeight - filterMinHeight, 160);
        const selectedFilters =  this._filters ? this._filters.filter( f => f.visible && f.selected).length : 0;

        if (this.filtersCount > 0) {
            const height = windowHeight - this.collapseFiltersHeight;
            if (selectedFilters === 0) {
                this.filterMaxHeight = height - FILTER_PADDING;
            } else {
                this.filterMaxHeight = Math.floor( height / selectedFilters) - FILTER_PADDING ;
            }


            this.filterMaxHeight = Math.max(this.filterMaxHeight, 120);
        }

    }

    get mobileDialogStyles(): any {
        const {top, left} = this.elementRef.nativeElement.getBoundingClientRect()
        let height = window.innerHeight;
        let width = window.innerWidth;
        if (this.filtersCount < 2 ) {
            height = window.innerHeight - top - 10;
            width = window.innerWidth - left - 18;
        }

        if (width > MAX_MOBILE_WIDTH) {
            width = MAX_MOBILE_WIDTH;
        }

        return {
            'width.px': Math.floor(width),
            'height.px': Math.floor(height),
            'position': this.filtersCount > 1 ? 'fixed' : 'absolute'
        };
    }

    isVisible(filterGroup: FilterGroup): boolean {
        return filterGroup.visible;
    }

    isFixedHeader(): boolean {
        return this.appSettings.settings.userFeatures['fixedHeader'] === true
    }

    private getMinHeight(headerHeight: number): number {
        return Math.max(0, window.innerHeight - FOOTER_HEIGHT - headerHeight);
    }


    get filtersCount(): number {
        return this._filters ? this._filters.filter( f =>  f.visible === true).length : 0;
    }

    private get collapseFiltersHeight(): number {
        if (!this._filters) {
            return 0;
        }

        return this._filters.reduce( (total: number, i: FilterGroup) => {
            if (!i.visible) {
                return total;
            }
            const filterHeight = i.selectedFilters.length > 0 ? 63 : 43;
            return total + filterHeight;
        }, 0);
    }

    clearAll() {
        this._filters.forEach( f => {
            f.clearSelection();
            f.clearDisplay();
            f.expanded = false;
        });

        this.update.emit(this._filters);
        this.closeMobileFilter();
        return false;
    }

    private getElementRect(): any {

        if (this.elementRef.nativeElement.children.length > 0) {
            if (this.elementRef.nativeElement.children[0].children.length > 0) {
                return this.elementRef.nativeElement.children[0].children[0].getBoundingClientRect();
            }
        }

        return {
            top: 0,
            height: 0
        }

    }
    get visible(): boolean {
        return !isEmptyArray(this._filters);
    }


    get hasSelectedFilters(): boolean {
        if (isEmptyArray(this._filters)) {
            return false;
        }

        return this._filters.reduce( (total, i) => (total += i.selectedFilters.length), 0) > 0;
    }


    toggleFiltersModal() {
        this.mobileOpened = !this.mobileOpened;
        return false;
    }

    closeMobileFilter() {
        if (this.mobileOpened) {
            this.toggleFiltersModal();
        }
    }

    get mobile(): boolean {
        return this.mobileCheck.mobile;
    }


    get sortedFiltersGroup(): FilterGroup[] {
        if (isEmptyArray(this._filters)) {
            return this._filters;
        }

        return  this._filters.sort( (a, b) => {
            return a.ord - b.ord;
        })
    }

}
